/**
 * The permissions of one Lookout member of a sibling workspace (ACT2-1124 LLD §5.5), in a drawer that the Users tab
 * opens from the member's row. Four switches, one for each grant set; a switch saves at once.
 *
 * - A RudderStack admin has every permission: all switches show on and disabled, with a callout, because RudderStack
 *   takes no grants for an admin.
 * - Only RudderStack admins can read and change grants. Anyone else sees their own grant sets read-only, from their own
 *   access policy (`viewerGrantSets`), and for other people sees why.
 * - While the sibling is paused, the switches are disabled and the drawer says why.
 * - "rETL Data Access" shows only while the activation failure chat is on for the workspace. Hidden, a grant the person
 *   already has is kept: a save sends the full set list, not only the switches shown.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';
import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { RudderstackReauthCallout } from '@/_shared/components/RudderstackReauthCallout';
import { Callout } from '@/_shared/components/ui/callout';
import { Label } from '@/_shared/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/_shared/components/ui/sheet';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { Switch } from '@/_shared/components/ui/switch';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useRudderstackSignInAgain } from '@/_shared/hooks/useRudderstackSignInAgain';
import { useSiblingStatus } from '@/_shared/hooks/useSiblingStatus';
import { orpcErrorMessage } from '@/_shared/lib/orpc-errors';
import { getWorkspaceSlug } from '@/_shared/lib/rpc';
import { RUDDERSTACK_GRANT_SETS } from '@/_shared/lib/rudderstack-grant-sets';
import { isRudderstackReauthRequired } from '@/_shared/lib/rudderstack-reauth';

type GrantsView = Awaited<ReturnType<RouterClient<AppRouter>['siblingGrants']['list']>>;
type GrantRow = GrantsView['rows'][number];
type GrantSetId = GrantRow['sets'][number];
type SaveInput = Parameters<RouterClient<AppRouter>['siblingGrants']['save']>[0];

const GRANT_SETS: { id: GrantSetId; label: string; description?: string }[] = RUDDERSTACK_GRANT_SETS;
const ALL_SETS = GRANT_SETS.map((set) => set.id);

const SUB_HEADING = 'text-xs font-bold tracking-wider text-muted-foreground uppercase';
const EDIT_INCLUDED = 'Create, delete and edit activations includes Edit audiences.';
const ADMIN_HAS_ALL =
  'RudderStack admins have every permission. RudderStack does not take permission changes for an admin.';
const ADMIN_SWITCH_REASON = 'A RudderStack admin has every permission.';
const NOT_RS_ADMIN = 'Only a RudderStack admin of the organization can see and change permissions.';
const OWN_READ_ONLY = 'Only a RudderStack admin of the organization can change permissions.';
const OWN_UNKNOWN = 'Lookout could not read your permissions from RudderStack. Try again later.';
const NO_ACCESS = 'No RudderStack access';
const INTRO = 'What this person can do with audiences and activations in RudderStack';
const INTRO_FAILED_RECORDS = ', and whether they can see the records a sync failed to send';

export interface PermissionsTarget {
  userId: string;
  name: string;
  email: string | null;
  /** The person is a RudderStack admin of the organization. */
  rudderstackAdmin: boolean;
}

interface MemberPermissionsDrawerProps {
  target: PermissionsTarget | null;
  /** The person who looks is a RudderStack admin of the organization. */
  viewerIsRsAdmin: boolean;
  onClose: () => void;
}

export function MemberPermissionsDrawer({
  target,
  viewerIsRsAdmin,
  onClose,
}: MemberPermissionsDrawerProps): React.ReactElement {
  // Like the rETL Data Access switch, the failed-records part of the intro shows only while the failure chat is on.
  const { isActivationFailureChatEnabled } = useWorkspace();
  return (
    <Sheet
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full gap-0 sm:max-w-md"
        onOpenAutoFocus={(event) => {
          // The first switch would take the focus and open its tooltip; the drawer itself takes it instead.
          event.preventDefault();
          (event.currentTarget as HTMLElement | null)?.focus();
        }}
      >
        <SheetHeader className="border-b border-border p-4">
          <SheetTitle className="truncate">{target?.name ?? ''}</SheetTitle>
          <SheetDescription className="truncate">{target?.email ?? ''}</SheetDescription>
        </SheetHeader>
        <div className="grid flex-1 content-start gap-4 overflow-y-auto p-4">
          <div>
            <h3 className={SUB_HEADING}>Permissions</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {INTRO}
              {isActivationFailureChatEnabled ? INTRO_FAILED_RECORDS : ''}.
            </p>
          </div>
          {target ? <PermissionsBody key={target.userId} target={target} viewerIsRsAdmin={viewerIsRsAdmin} /> : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function PermissionsBody({
  target,
  viewerIsRsAdmin,
}: {
  target: PermissionsTarget;
  viewerIsRsAdmin: boolean;
}): React.ReactElement {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const siblingStatus = useSiblingStatus();
  // ACT2-1124: an ended RudderStack sign-in offers "Sign in again".
  const signIn = useRudderstackSignInAgain();
  const grantsQuery = useQuery({
    ...orpc.siblingGrants.list.queryOptions({ input: {} }),
    enabled: viewerIsRsAdmin && !target.rudderstackAdmin,
  });
  // One key per member. The save outlives this body, which unmounts with the drawer, so a drawer that opens again
  // during the save finds it in the mutation cache.
  const saveKey = [...orpc.siblingGrants.save.mutationKey(), target.userId];
  // The sets of the last click, shown at once until the save and the reload of the grants end. The switches stay busy
  // until then, so the next click starts from the saved sets.
  const pendingSets = useMutationState({
    filters: { mutationKey: saveKey, status: 'pending' },
    select: (mutation) => (mutation.state.variables as SaveInput).sets,
  }).at(-1);
  const save = useMutation(
    orpc.siblingGrants.save.mutationOptions({
      mutationKey: saveKey,
      onMutate: () => ({ workspaceSlug: getWorkspaceSlug() }),
      onSuccess: (_data, _input, started) => {
        toast.success('Permissions saved');
        // Returned, so the save stays pending until the reload ends. `all` reloads the grants also while the drawer is
        // closed, so a drawer that opens after the save shows the saved sets. The client reads the workspace from the
        // URL, so after a switch to another workspace the grants are only marked old: a reload would store the other
        // workspace's grants under this one's key.
        const sameWorkspace = started?.workspaceSlug === getWorkspaceSlug();
        return queryClient.invalidateQueries({
          queryKey: orpc.siblingGrants.key(),
          refetchType: sameWorkspace ? 'all' : 'none',
        });
      },
      onError: (err) => {
        if (!signIn.toastIfReauth(err)) toast.error(orpcErrorMessage(err, 'Could not save the permissions'));
      },
    }),
  );

  if (target.rudderstackAdmin) {
    return (
      <>
        <Callout variant="info">{ADMIN_HAS_ALL}</Callout>
        <GrantSwitches idPrefix={target.userId} sets={ALL_SETS} disabledReason={ADMIN_SWITCH_REASON} />
      </>
    );
  }
  if (!viewerIsRsAdmin) {
    // A person who is not an RS admin sees only their own permissions, read-only, from their own access policy.
    if (target.userId !== user?.id) return <Callout variant="info">{NOT_RS_ADMIN}</Callout>;
    const ownSets = siblingStatus?.viewerGrantSets ?? null;
    if (ownSets === null) return <Callout variant="info">{OWN_UNKNOWN}</Callout>;
    return <GrantSwitches idPrefix={target.userId} sets={ownSets} disabledReason={OWN_READ_ONLY} />;
  }

  if (grantsQuery.isPending) {
    return (
      <div className="flex flex-col gap-2" role="status" aria-label="Loading permissions">
        <Skeleton className="h-6 w-full rounded-md" />
        <Skeleton className="h-6 w-full rounded-md" />
        <Skeleton className="h-6 w-full rounded-md" />
        <Skeleton className="h-6 w-full rounded-md" />
      </div>
    );
  }
  if (grantsQuery.isError) {
    return isRudderstackReauthRequired(grantsQuery.error) ? (
      <RudderstackReauthCallout />
    ) : (
      <Callout variant="error">{orpcErrorMessage(grantsQuery.error, 'Could not load the permissions.')}</Callout>
    );
  }

  const { readOnlyReason } = grantsQuery.data;
  const row = grantsQuery.data.rows.find((r) => r.lookoutUserId === target.userId);
  if (row?.access === 'full') {
    return (
      <>
        <Callout variant="info">{ADMIN_HAS_ALL}</Callout>
        <GrantSwitches idPrefix={target.userId} sets={ALL_SETS} disabledReason={ADMIN_SWITCH_REASON} />
      </>
    );
  }
  if (!row || row.access === 'none' || row.rsUserId === null) {
    const reason = row?.noAccessReason ?? NO_ACCESS;
    return (
      <>
        <Callout variant="warning">{reason}</Callout>
        <GrantSwitches idPrefix={target.userId} sets={[]} disabledReason={reason} />
      </>
    );
  }
  const rsUserId = row.rsUserId;
  return (
    <>
      {readOnlyReason ? <Callout variant="warning">{readOnlyReason}</Callout> : null}
      <GrantSwitches
        idPrefix={target.userId}
        sets={pendingSets ?? row.sets}
        disabledReason={readOnlyReason}
        saving={pendingSets !== undefined}
        onChange={(sets) => save.mutate({ userId: rsUserId, sets })}
      />
    </>
  );
}

function GrantSwitches({
  idPrefix,
  sets,
  disabledReason,
  saving = false,
  onChange,
}: {
  idPrefix: string;
  sets: readonly GrantSetId[];
  /** Every switch is disabled, with this reason in its tooltip. */
  disabledReason: string | null;
  saving?: boolean;
  onChange?: (sets: GrantSetId[]) => void;
}): React.ReactElement {
  const { isActivationFailureChatEnabled } = useWorkspace();
  const shown = isActivationFailureChatEnabled ? GRANT_SETS : GRANT_SETS.filter((set) => set.id !== 'retl_data_access');
  const activationsOn = sets.includes('activations');
  return (
    <div className="flex flex-col gap-3">
      {shown.map((set) => {
        const implied = set.id === 'audiences_edit' && activationsOn;
        const checked = implied || sets.includes(set.id);
        const reason = disabledReason ?? (implied ? EDIT_INCLUDED : null);
        const id = `grant-${idPrefix}-${set.id}`;
        return (
          <div key={set.id} className="flex items-start gap-2">
            <ActionTooltip show={reason !== null} message={reason ?? ''}>
              <Switch
                id={id}
                checked={checked}
                loading={saving}
                disabled={reason !== null || onChange === undefined}
                onCheckedChange={(next) => {
                  onChange?.(next ? [...sets, set.id] : sets.filter((s) => s !== set.id));
                }}
              />
            </ActionTooltip>
            <div className="grid gap-0.5">
              <Label htmlFor={id} className="text-sm font-normal text-foreground">
                {set.label}
              </Label>
              {set.description ? <p className="text-xs text-muted-foreground">{set.description}</p> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
